import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// API base configuration
const API_BASE_URL = '/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
});

// Request interceptor - add JWT token
apiClient.interceptors.request.use(
  (config) => {
    const headers = AxiosHeaders.from(config.headers || {});
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      // Let the browser set multipart/form-data with the correct boundary.
      headers.delete('Content-Type');
      headers.delete('content-type');
    } else if (!headers.has('Content-Type') && !headers.has('content-type')) {
      headers.set('Content-Type', 'application/json');
    }
    const token = localStorage.getItem('access_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    config.headers = headers;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    if (!error.response) {
      // Network error
      console.error('Network error:', error.message);
      return Promise.reject(new Error('网络连接失败，请检查网络设置'));
    }

    const { status } = error.response;

    switch (status) {
      case 400:
      case 413:
      case 409: {
        // Bad request / conflict
        const errData = error.response.data as { error?: string; message?: string };
        return Promise.reject(new Error(errData.error || errData.message || '请求失败，请检查输入参数'));
      }

      case 401: {
        if (window.location.pathname.startsWith('/sso/callback')) break;
        const raw = localStorage.getItem('access_token');
        if (!raw) {
          // 已登出（蒙层覆盖中），不跳页，让蒙层处理重新登录
          break;
        }
        // token 存在但服务端拒绝（过期 or 被 gatekeeper back-channel 撤销）→ 强制重新登录
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_info');
        import('../store/useAppStore').then(({ useAppStore }) => {
          useAppStore.setState({
            permissionCodes: [],
            permissionsLoaded: false,
            permissionsLoading: false,
            isLoggedOut: true,
          });
        });
        break;
      }

      case 403: {
        console.error('Access forbidden:', error.response.data);
        const d403 = error.response.data as { error?: string; message?: string } | undefined;
        const forbiddenErr = new Error(d403?.error || d403?.message || '没有权限执行此操作');
        (forbiddenErr as any).status = 403;
        return Promise.reject(forbiddenErr);
      }

      case 404: {
        console.error('Resource not found:', error.response.data);
        const d404 = error.response.data as { error?: string; message?: string } | undefined;
        return Promise.reject(new Error(d404?.error || d404?.message || '请求的资源不存在'));
      }

      case 422:
        // Validation error
        const validationError = error.response.data as { detail?: Array<{ msg: string }> };
        const message = validationError.detail?.[0]?.msg || '请求参数验证失败';
        return Promise.reject(new Error(message));

      case 429:
        // Rate limited
        console.error('Rate limited:', error.response.data);
        return Promise.reject(new Error('请求过于频繁，请稍后再试'));

      case 500: {
        console.error('Server error:', error.response.data);
        const d500 = error.response.data as { error?: string; message?: string } | undefined;
        return Promise.reject(new Error(d500?.error || d500?.message || '服务器内部错误，请稍后重试'));
      }
      case 502:
      case 504:
        // 上游（第三方广告平台）接口异常
        console.error('Upstream error:', error.response.data);
        return Promise.reject(new Error('第三方广告平台接口异常，请稍后重试'));
      case 503:
        console.error('Service unavailable:', error.response.data);
        const d503 = error.response.data as { error?: string; message?: string } | undefined;
        return Promise.reject(new Error(d503?.error || d503?.message || '服务暂时不可用，请稍后重试'));

      default:
        console.error(`HTTP ${status} error:`, error.response.data);
        return Promise.reject(new Error('请求失败，请重试'));
    }

    return Promise.reject(error);
  }
);

// Export typed request methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};

export default apiClient;
